import React, { useEffect, useMemo } from "react";
import { AvailableFareConnectionModel, AvailableFareModel } from "WebApiClient";
import FareLegConnectionTableRow from "./Fare.Leg.ConTbl.Row";
import _ from "lodash";

interface ConnectionTableProps {
  Data: AvailableFareConnectionModel[];
  SelectedConnection: number;
  GetSelectedConnectionIndexByLegIndex: any;
  LegIndex: number;
  SelectConnection: (legIndex: number, connectionIndex: number) => void;
  IsWebFare: boolean;
  ApplicableConnections?: number[];
  Fare: AvailableFareModel;
  setStatus: (status: boolean) => void;
  headerOffVisible: boolean;
  upscaleLength: number;
}

const FareLegConnectionTable: React.FC<ConnectionTableProps> = ({
  Data,
  SelectedConnection,
  GetSelectedConnectionIndexByLegIndex,
  LegIndex,
  SelectConnection,
  IsWebFare,
  ApplicableConnections,
  Fare,
  setStatus,
  headerOffVisible,
}) => {
  useEffect(() => {
    setStatus(true);
  }, [setStatus]);

  const hasAllotment = useMemo(() => {
    return Data.some((con) =>
      con.segments.some((seg) => !!seg.allotmentInformation)
    );
  }, [Data]);

  const areIncludedServicesEqual = (
    obj1: any,
    obj2: any,
    key: string
  ): boolean => {
    const val1 = _.get(obj1, key, []);
    const val2 = _.get(obj2, key, []);

    if (key === "cabinClassName") return val1 === val2;

    if (
      (typeof val1 !== "string" || typeof val2 !== "string") &&
      (!val1.length || !val2.length)
    )
      return true;

    if (
      (typeof val1 === "string" || typeof val2 === "string") &&
      (!val1 || !val2)
    )
      return true;

    return _.isEqual(val1, val2);
  };

  const areAllIncludedServicesEqual = useMemo(() => {
    if (Data.length < 2) {
      const segments = _.get(Data[0], "segments", []);
      for (let j = 0; j < segments.length - 1; j++) {
        if (
          areIncludedServicesEqual(segments[j], segments[j + 1], "cabinClassName")
        ) {
          return true;
        }
      }
      return false;
    }

    for (let i = 1; i < Data.length; i++) {
      const seg1 = _.get(Data[0], "segments", []);
      const seg2 = _.get(Data[i], "segments", []);

      if (seg1.length !== seg2.length) return false;

      for (let j = 0; j < seg1.length; j++) {
        if (!areIncludedServicesEqual(seg1[j], seg2[j], "cabinClassName")) {
          return false;
        }
      }
    }
    return true;
  }, [Data]);

  const selectedConnection = Data[SelectedConnection];

  return (
    <>
      {!headerOffVisible ? (
        <div className="upSellCard">
          {selectedConnection && (
            <FareLegConnectionTableRow
              Data={selectedConnection}
              SelectedConnection={SelectedConnection}
              GetSelectedConnectionIndexByLegIndex={GetSelectedConnectionIndexByLegIndex}
              connection={selectedConnection}
              LegIndex={LegIndex}
              ConnectionIndex={selectedConnection.index}
              SelectConnection={SelectConnection}
              IsWebFare={IsWebFare}
              connectionAppliesToFilter={
                ApplicableConnections?.includes(selectedConnection.index) ?? true
              }
              Fare={Fare}
              cabinIsPresentAll={areAllIncludedServicesEqual}
              headerOffVisible={headerOffVisible}
            />
          )}
        </div>
      ) : (
        <div
          style={{ overflow: "auto" }}
          className="multi-collapse segment__moreFlightTimes"
        >
          <table className="table table-sm table-bordered table-responsive-xl">
            <thead className="vertical-table">
              <tr>
                <th style={{ width: "1%" }}></th>
                <th>Routing</th>
                <th>Flight</th>
                <th>Type</th>
                <th>Date</th>
                <th>Time</th>
                <th>RBD</th>
                <th>Baggage</th>
                <th>Incl.Services</th>
                {hasAllotment && <th>A</th>}
              </tr>
            </thead>
            <tbody>
              {Data.map((connection, index) => (
                <FareLegConnectionTableRow
                  key={`FareLegConnectionTableRow-${index}`}
                  Data={connection}
                  SelectedConnection={SelectedConnection}
                  GetSelectedConnectionIndexByLegIndex={
                    GetSelectedConnectionIndexByLegIndex
                  }
                  connection={Data}
                  LegIndex={LegIndex}
                  ConnectionIndex={connection.index}
                  SelectConnection={SelectConnection}
                  IsWebFare={IsWebFare}
                  connectionAppliesToFilter={
                    ApplicableConnections?.includes(connection.index) ?? true
                  }
                  Fare={Fare}
                  cabinIsPresentAll={areAllIncludedServicesEqual}
                  headerOffVisible={headerOffVisible}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default FareLegConnectionTable;
